/**
 * @description: base64 to blob
 */
export function dataURLtoBlob(base64Buf: string): Blob {
  const arr = base64Buf.split(',');
  const typeItem = arr[0];
  const mime = typeItem.match(/:(.*?);/)![1];
  const bstr = window.atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

/**
 * img url to base64
 * @param url
 */
export function urlToBase64(url: string, mineType?: string): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      let canvas = document.createElement('CANVAS') as Nullable<HTMLCanvasElement>;
      const ctx = canvas!.getContext('2d');

      const img = new Image();
      img.crossOrigin = '';
      img.onload = function () {
        if (!canvas || !ctx) {
          return reject();
        }
        canvas.height = img.height;
        canvas.width = img.width;
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL(mineType || 'image/png');
        canvas = null;
        resolve(dataURL);
      };

      img.onerror = function (e) {
        reject(e);
      };
      img.src = url;
    } catch (e) {
      reject(e);
    }
  });
}

export async function getSVGContent(url): Promise<string> {
  return new Promise((r, reject) => {
    fetch(url)
      .then((response) => response.text())
      .then((svgContent) => {
        r(svgContent);
      })
      .catch((error) => reject(error));
  });
}

/** 1k/2k/4k/8k → 最长边像素 */
const SVG_PNG_SCALE_MAP: Record<number, number> = {
  1: 1024,
  2: 2048,
  4: 4096,
  8: 8192,
};

function parseSvgSize(svgText: string): { width: number; height: number } | null {
  try {
    const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    const svg = doc.documentElement;
    if (!svg || svg.tagName.toLowerCase() !== 'svg') return null;

    const parseLen = (v: string | null) => {
      if (!v) return NaN;
      const n = parseFloat(v);
      return Number.isFinite(n) && n > 0 ? n : NaN;
    };

    const w = parseLen(svg.getAttribute('width'));
    const h = parseLen(svg.getAttribute('height'));
    if (w && h) return { width: w, height: h };

    const viewBox = svg.getAttribute('viewBox')?.trim().split(/[\s,]+/).map(Number);
    if (viewBox && viewBox.length >= 4 && viewBox[2] > 0 && viewBox[3] > 0) {
      return { width: viewBox[2], height: viewBox[3] };
    }
  } catch {
    // ignore parse errors, fall back to image natural size
  }
  return null;
}

function calcPngSize(
  srcW: number,
  srcH: number,
  scale: number
): { width: number; height: number } {
  const targetLongEdge = SVG_PNG_SCALE_MAP[scale] ?? scale * 1024;
  const ratio = targetLongEdge / Math.max(srcW, srcH, 1);
  return {
    width: Math.max(1, Math.round(srcW * ratio)),
    height: Math.max(1, Math.round(srcH * ratio)),
  };
}

/**
 * SVG 转 PNG。scale 为 1/2/4/8 时，按最长边输出 1k/2k/4k/8k。
 */
export async function svgToPng(options: { url: string; scale: number }): Promise<Blob> {
  const { url, scale } = options;
  const svgText = await getSVGContent(url);
  const parsed = parseSvgSize(svgText);

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);

    img.onload = function () {
      try {
        const srcW = parsed?.width || img.naturalWidth || img.width || 1;
        const srcH = parsed?.height || img.naturalHeight || img.height || 1;
        const { width, height } = calcPngSize(srcW, srcH, scale);

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('无法创建画布'));
          return;
        }
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(function (result: Blob | null) {
          if (!result) {
            reject(new Error('PNG 导出失败'));
            return;
          }
          resolve(result);
        }, 'image/png');
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };

    img.onerror = function (e) {
      URL.revokeObjectURL(objectUrl);
      reject(e);
    };
    img.src = objectUrl;
  });
}
